/* ── Bússola: Language Mind-Map Data ── */

export interface BussolaWord {
  en: string;
  pt: string;
}

export interface BussolaPattern {
  label: string;        // e.g. "Passado Simples"
  pattern: string;      // e.g. "I + verb(ed)"
  example: string;      // e.g. "I swam yesterday"
  examplePt: string;    // e.g. "Eu nadei ontem"
}

export interface BussolaSubcategory {
  id: string;
  label: string;
  labelPt: string;
  /** Grammar patterns (for Tempo, Perguntas, Conectar, Situacoes) */
  patterns?: BussolaPattern[];
  /** Vocabulary chips (for Acoes, Descrever) */
  words?: BussolaWord[];
}

export interface BussolaCategory {
  id: string;
  label: string;
  labelPt: string;
  icon: string;          // FontAwesome class
  color: string;         // accent color for the category
  subcategories: BussolaSubcategory[];
}

export const bussolaData: BussolaCategory[] = [
  /* ─── 1. TEMPO ─── */
  {
    id: 'tempo',
    label: 'Tempo',
    labelPt: 'Quando aconteceu?',
    icon: 'fa-solid fa-clock',
    color: '#7932b6',
    subcategories: [
      {
        id: 'passado',
        label: 'Passado',
        labelPt: 'O que já aconteceu',
        patterns: [
          { label: 'Simples', pattern: 'I + verb(ed)', example: 'I worked yesterday', examplePt: 'Eu trabalhei ontem' },
          { label: 'Contínuo', pattern: 'I was + verb(ing)', example: 'I was swimming', examplePt: 'Eu estava nadando' },
          { label: 'Experiência', pattern: 'I have + verb(ed)', example: 'I have visited Paris', examplePt: 'Eu já visitei Paris' },
        ],
      },
      {
        id: 'presente',
        label: 'Presente',
        labelPt: 'O que acontece agora',
        patterns: [
          { label: 'Rotina', pattern: 'I + verb', example: 'I work every day', examplePt: 'Eu trabalho todo dia' },
          { label: 'Agora', pattern: 'I am + verb(ing)', example: 'I am studying English', examplePt: 'Eu estou estudando inglês' },
        ],
      },
      {
        id: 'futuro',
        label: 'Futuro',
        labelPt: 'O que vai acontecer',
        patterns: [
          { label: 'Plano', pattern: "I'm going to + verb", example: "I'm going to travel", examplePt: 'Eu vou viajar' },
          { label: 'Decisão', pattern: 'I will + verb', example: 'I will help you', examplePt: 'Eu vou te ajudar' },
        ],
      },
    ],
  },

  /* ─── 2. AÇÕES ─── */
  {
    id: 'acoes',
    label: 'Ações',
    labelPt: 'Como dizer esse verbo?',
    icon: 'fa-solid fa-bolt',
    color: '#ff9301',
    subcategories: [
      {
        id: 'dia-a-dia',
        label: 'Dia a dia',
        labelPt: 'Rotina diária',
        words: [
          { en: 'eat', pt: 'comer' }, { en: 'drink', pt: 'beber' }, { en: 'sleep', pt: 'dormir' },
          { en: 'wake up', pt: 'acordar' }, { en: 'cook', pt: 'cozinhar' }, { en: 'clean', pt: 'limpar' },
          { en: 'buy', pt: 'comprar' }, { en: 'go', pt: 'ir' }, { en: 'come', pt: 'vir' },
          { en: 'take', pt: 'pegar / levar' }, { en: 'leave', pt: 'sair' }, { en: 'arrive', pt: 'chegar' },
          { en: 'drive', pt: 'dirigir' }, { en: 'walk', pt: 'andar / caminhar' }, { en: 'wait', pt: 'esperar' },
          { en: 'pay', pt: 'pagar' },
        ],
      },
      {
        id: 'esportes',
        label: 'Esportes',
        labelPt: 'Atividades físicas',
        words: [
          { en: 'swim', pt: 'nadar' }, { en: 'run', pt: 'correr' }, { en: 'play', pt: 'jogar' },
          { en: 'jump', pt: 'pular' }, { en: 'ride', pt: 'andar (bicicleta)' }, { en: 'throw', pt: 'lançar' },
          { en: 'catch', pt: 'pegar' }, { en: 'kick', pt: 'chutar' }, { en: 'climb', pt: 'escalar' },
          { en: 'stretch', pt: 'alongar' }, { en: 'lift', pt: 'levantar' }, { en: 'train', pt: 'treinar' },
        ],
      },
      {
        id: 'comunicacao',
        label: 'Comunicação',
        labelPt: 'Falar e ouvir',
        words: [
          { en: 'say', pt: 'dizer' }, { en: 'tell', pt: 'contar' }, { en: 'talk', pt: 'conversar' },
          { en: 'speak', pt: 'falar' }, { en: 'ask', pt: 'perguntar' }, { en: 'answer', pt: 'responder' },
          { en: 'explain', pt: 'explicar' }, { en: 'listen', pt: 'ouvir' }, { en: 'understand', pt: 'entender' },
          { en: 'mean', pt: 'significar' }, { en: 'agree', pt: 'concordar' }, { en: 'promise', pt: 'prometer' },
        ],
      },
      {
        id: 'trabalho',
        label: 'Trabalho',
        labelPt: 'No trabalho',
        words: [
          { en: 'work', pt: 'trabalhar' }, { en: 'send', pt: 'enviar' }, { en: 'create', pt: 'criar' },
          { en: 'manage', pt: 'gerenciar' }, { en: 'lead', pt: 'liderar' }, { en: 'meet', pt: 'encontrar / reunir' },
          { en: 'decide', pt: 'decidir' }, { en: 'finish', pt: 'terminar' }, { en: 'start', pt: 'começar' },
          { en: 'hire', pt: 'contratar' }, { en: 'solve', pt: 'resolver' }, { en: 'develop', pt: 'desenvolver' },
        ],
      },
      {
        id: 'sentimentos-verbos',
        label: 'Sentir',
        labelPt: 'Emoções como ação',
        words: [
          { en: 'feel', pt: 'sentir' }, { en: 'love', pt: 'amar' }, { en: 'like', pt: 'gostar' },
          { en: 'hate', pt: 'odiar' }, { en: 'miss', pt: 'sentir falta' }, { en: 'worry', pt: 'preocupar' },
          { en: 'hope', pt: 'esperar (desejar)' }, { en: 'enjoy', pt: 'aproveitar' }, { en: 'prefer', pt: 'preferir' },
          { en: 'need', pt: 'precisar' }, { en: 'want', pt: 'querer' }, { en: 'wish', pt: 'desejar' },
        ],
      },
    ],
  },

  /* ─── 3. DESCREVER ─── */
  {
    id: 'descrever',
    label: 'Descrever',
    labelPt: 'Como descrever algo?',
    icon: 'fa-solid fa-palette',
    color: '#e84393',
    subcategories: [
      {
        id: 'sentimentos',
        label: 'Sentimentos',
        labelPt: 'Como estou me sentindo',
        words: [
          { en: 'happy', pt: 'feliz' }, { en: 'sad', pt: 'triste' }, { en: 'tired', pt: 'cansado' },
          { en: 'excited', pt: 'animado' }, { en: 'nervous', pt: 'nervoso' }, { en: 'angry', pt: 'com raiva' },
          { en: 'scared', pt: 'com medo' }, { en: 'surprised', pt: 'surpreso' }, { en: 'bored', pt: 'entediado' },
          { en: 'proud', pt: 'orgulhoso' }, { en: 'confused', pt: 'confuso' }, { en: 'calm', pt: 'calmo' },
        ],
      },
      {
        id: 'aparencia',
        label: 'Aparência',
        labelPt: 'Como algo é',
        words: [
          { en: 'big', pt: 'grande' }, { en: 'small', pt: 'pequeno' }, { en: 'tall', pt: 'alto' },
          { en: 'short', pt: 'baixo / curto' }, { en: 'beautiful', pt: 'bonito' }, { en: 'ugly', pt: 'feio' },
          { en: 'fast', pt: 'rápido' }, { en: 'slow', pt: 'lento' }, { en: 'old', pt: 'velho' },
          { en: 'new', pt: 'novo' }, { en: 'easy', pt: 'fácil' }, { en: 'hard', pt: 'difícil' },
          { en: 'hot', pt: 'quente' }, { en: 'cold', pt: 'frio' }, { en: 'expensive', pt: 'caro' },
          { en: 'cheap', pt: 'barato' },
        ],
      },
      {
        id: 'opiniao',
        label: 'Opinião',
        labelPt: 'Expressar o que penso',
        patterns: [
          { label: 'Eu acho', pattern: 'I think + frase', example: 'I think it\'s a good idea', examplePt: 'Eu acho que é uma boa ideia' },
          { label: 'Eu acredito', pattern: 'I believe + frase', example: 'I believe we can do it', examplePt: 'Eu acredito que conseguimos' },
          { label: 'Na minha opinião', pattern: 'In my opinion, ...', example: 'In my opinion, it\'s too expensive', examplePt: 'Na minha opinião, é caro demais' },
          { label: 'Parece que', pattern: 'It seems like ...', example: 'It seems like a good plan', examplePt: 'Parece ser um bom plano' },
        ],
      },
    ],
  },

  /* ─── 4. PERGUNTAS ─── */
  {
    id: 'perguntas',
    label: 'Perguntas',
    labelPt: 'Como perguntar?',
    icon: 'fa-solid fa-circle-question',
    color: '#0984e3',
    subcategories: [
      {
        id: 'basicas',
        label: 'Perguntas básicas',
        labelPt: 'As mais usadas',
        patterns: [
          { label: 'O quê?', pattern: 'What ...?', example: 'What do you do?', examplePt: 'O que você faz?' },
          { label: 'Onde?', pattern: 'Where ...?', example: 'Where do you live?', examplePt: 'Onde você mora?' },
          { label: 'Quando?', pattern: 'When ...?', example: 'When did you arrive?', examplePt: 'Quando você chegou?' },
          { label: 'Por quê?', pattern: 'Why ...?', example: 'Why did you leave?', examplePt: 'Por que você saiu?' },
          { label: 'Como?', pattern: 'How ...?', example: 'How are you?', examplePt: 'Como você está?' },
          { label: 'Quem?', pattern: 'Who ...?', example: 'Who told you that?', examplePt: 'Quem te disse isso?' },
        ],
      },
      {
        id: 'quantidade',
        label: 'Quantidade',
        labelPt: 'Quanto / Quantos',
        patterns: [
          { label: 'Quanto?', pattern: 'How much ...?', example: 'How much does it cost?', examplePt: 'Quanto custa?' },
          { label: 'Quantos?', pattern: 'How many ...?', example: 'How many people came?', examplePt: 'Quantas pessoas vieram?' },
          { label: 'Quanto tempo?', pattern: 'How long ...?', example: 'How long have you lived here?', examplePt: 'Há quanto tempo você mora aqui?' },
        ],
      },
    ],
  },

  /* ─── 5. CONECTAR ─── */
  {
    id: 'conectar',
    label: 'Conectar',
    labelPt: 'Ligar duas ideias',
    icon: 'fa-solid fa-link',
    color: '#00b894',
    subcategories: [
      {
        id: 'conectores',
        label: 'Conectores',
        labelPt: 'Palavras de ligação',
        patterns: [
          { label: 'Mas', pattern: 'but', example: 'I like it, but it\'s expensive', examplePt: 'Eu gosto, mas é caro' },
          { label: 'Porque', pattern: 'because', example: 'I stayed home because it was raining', examplePt: 'Fiquei em casa porque estava chovendo' },
          { label: 'Então', pattern: 'so', example: 'I was tired, so I went to bed', examplePt: 'Eu estava cansado, então fui dormir' },
          { label: 'Embora', pattern: 'although / even though', example: 'Although it was cold, I went out', examplePt: 'Embora estivesse frio, eu saí' },
          { label: 'Porém', pattern: 'however', example: 'I wanted to go. However, I was busy', examplePt: 'Eu queria ir. Porém, estava ocupado' },
          { label: 'Além disso', pattern: 'also / besides', example: "It's fun and also cheap", examplePt: 'É divertido e também barato' },
          { label: 'Por isso', pattern: "that's why", example: "I studied a lot. That's why I passed", examplePt: 'Estudei muito. Por isso passei' },
        ],
      },
    ],
  },

  /* ─── 6. SITUAÇÕES ─── */
  {
    id: 'situacoes',
    label: 'Situações',
    labelPt: 'O que dizer quando...',
    icon: 'fa-solid fa-comment-dots',
    color: '#6c5ce7',
    subcategories: [
      {
        id: 'concordar',
        label: 'Concordar',
        labelPt: 'Quando você concorda',
        patterns: [
          { label: 'Concordo', pattern: 'I agree', example: 'I agree with you', examplePt: 'Eu concordo com você' },
          { label: 'Exatamente', pattern: 'Exactly / That\'s right', example: 'Exactly! That\'s what I think', examplePt: 'Exatamente! É o que eu acho' },
          { label: 'Com certeza', pattern: 'Definitely / For sure', example: 'Definitely, let\'s do it', examplePt: 'Com certeza, vamos fazer' },
        ],
      },
      {
        id: 'discordar',
        label: 'Discordar',
        labelPt: 'Quando você discorda',
        patterns: [
          { label: 'Discordo', pattern: "I don't think so", example: "I don't think that's right", examplePt: 'Não acho que esteja certo' },
          { label: 'Não concordo', pattern: 'I disagree', example: 'I disagree with that idea', examplePt: 'Eu discordo dessa ideia' },
          { label: 'Educado', pattern: "I see your point, but...", example: 'I see your point, but I think differently', examplePt: 'Entendo seu ponto, mas penso diferente' },
        ],
      },
      {
        id: 'nao-entendi',
        label: 'Não entendi',
        labelPt: 'Pedir para repetir',
        patterns: [
          { label: 'Repetir', pattern: 'Can you repeat that?', example: 'Sorry, can you repeat that?', examplePt: 'Desculpa, pode repetir?' },
          { label: 'Mais devagar', pattern: 'Can you speak slower?', example: 'Can you speak a little slower?', examplePt: 'Pode falar mais devagar?' },
          { label: 'O que significa?', pattern: 'What does ... mean?', example: 'What does "deadline" mean?', examplePt: 'O que significa "deadline"?' },
        ],
      },
      {
        id: 'pedir-ajuda',
        label: 'Pedir ajuda',
        labelPt: 'Quando não sabe a palavra',
        patterns: [
          { label: 'Como se diz?', pattern: 'How do you say ...?', example: 'How do you say "nadar" in English?', examplePt: 'Como se diz "nadar" em inglês?' },
          { label: 'Qual a palavra?', pattern: "What's the word for ...?", example: "What's the word for someone who cooks?", examplePt: 'Qual a palavra para quem cozinha?' },
          { label: 'Está certo?', pattern: 'Is it correct to say ...?', example: 'Is it correct to say "I goed"?', examplePt: 'Está certo dizer "I goed"?' },
        ],
      },
    ],
  },
];
